import { type FunctionComponent } from 'react'
import React from 'react'
import styled from '@emotion/styled'
import {
  typography,
  ItemsList,
  Divider,
  Flex,
  Text,
  spacing,
} from '@retailer-platform/shared-components/src/tds'
import { usePartnerContext } from '@retailer-platform/dashboard/utils'
import { useDomainMessages } from '../../../../utils/domain/intl'
import { COLUMN_WIDTHS } from '../types'
import { type NotificationsNotificationChannel } from '../../../../api'
import { useIsInAppEnabled } from '../hooks/useIsInAppEnabled.hooks'
import { NotificationSettingRow } from './NotificationSettingRow'

const ColumnHeader = styled.div({
  ...typography.bodyEmphasized,
})

const SectionDescription = styled.div(({ theme }) => ({
  marginBottom: '24px',
  color: theme.colors.systemGrayscale80,
}))

const InfoMessage = styled.div(({ theme }) => ({
  color: theme.colors.systemGrayscale80,
  marginBottom: '16px',
}))

const PartnerHeaderWrapper = styled.div({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-start',
})

export const PartnerChannelsSection: FunctionComponent<{
  partnerChannels: NotificationsNotificationChannel[]
}> = ({ partnerChannels }) => {
  const partnerContext = usePartnerContext()

  const i18n = useDomainMessages({
    partnerTitle: 'notificationsDomain.list.partnerSettings.pageTitle',
    partnerDesc: 'notificationsDomain.list.partnerSettings.pageDescription',
    eventName: 'notificationsDomain.list.columnTitle.eventName',
    description: 'notificationsDomain.list.columnTitle.description',
    partner: 'notificationsDomain.list.columnTitle.partner',
    email: 'notificationsDomain.list.columnTitle.email',
    inApp: 'notificationsDomain.list.columnTitle.inApp',
    noNotifications: 'notificationsDomain.list.noNotifications',
  })

  const isInAppEnabled = useIsInAppEnabled()

  return (
    <>
      <PartnerHeaderWrapper>
        <Text typography="title" css={{ marginBottom: '8px', marginTop: '32px' }}>
          {i18n.partnerTitle}
        </Text>
        <SectionDescription>
          <Text typography="bodyRegular">{i18n.partnerDesc}</Text>
        </SectionDescription>
      </PartnerHeaderWrapper>
      {partnerChannels.length === 0 ? (
        <InfoMessage>{i18n.noNotifications}</InfoMessage>
      ) : (
        <>
          <ItemsList.Section>
            <Flex.Row gap={spacing.s8} alignItems="center">
              <Flex.Column css={{ width: COLUMN_WIDTHS.EVENT_NAME }}>
                <ColumnHeader>{i18n.eventName}</ColumnHeader>
              </Flex.Column>
              <Flex.Column css={{ width: COLUMN_WIDTHS.DESCRIPTION }}>
                <ColumnHeader>{i18n.description}</ColumnHeader>
              </Flex.Column>
              <Flex.Column css={{ width: COLUMN_WIDTHS.PARTNER }}>
                <ColumnHeader>{i18n.partner}</ColumnHeader>
              </Flex.Column>
              <Flex.Column css={{ width: COLUMN_WIDTHS.EMAIL }}>
                <ColumnHeader>{i18n.email}</ColumnHeader>
              </Flex.Column>
              {isInAppEnabled && (
                <Flex.Column css={{ width: COLUMN_WIDTHS.IN_APP }}>
                  <ColumnHeader>{i18n.inApp}</ColumnHeader>
                </Flex.Column>
              )}
              <Flex.Column css={{ width: COLUMN_WIDTHS.BANNER }}>
                <ColumnHeader></ColumnHeader>
              </Flex.Column>
            </Flex.Row>
          </ItemsList.Section>
          <Divider />
          {partnerChannels.map((notification, index) => (
            <React.Fragment key={`partner-${notification.id}`}>
              <NotificationSettingRow
                channel={notification}
                partnerName={partnerContext?.partnerName || 'Unknown'}
                scopeId={partnerContext?.partner?.id}
                showInApp={isInAppEnabled}
              />
              {index < partnerChannels.length - 1 && <Divider />}
            </React.Fragment>
          ))}
        </>
      )}
    </>
  )
}
